import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  List,
  Text,
} from "@shopify/polaris";
import { useCallback, useMemo, useRef, useState } from "react";

import {
  applyOfficialExport,
  dryRunOfficialExport,
  OFFICIAL_EXPORT_FILE_NAMES,
  type OfficialExportFileName,
  type OfficialExportImportResult,
} from "../../api/stocky_official_export_import";
import { ApiError } from "../../api/client";
import { csvErrorMessage } from "../../components/csvImportSpec";
import { t } from "../../lib/i18n";

interface Props {
  onImported: () => void;
}

export interface OfficialExportFileValidation {
  missing: readonly OfficialExportFileName[];
  unknown: readonly string[];
  duplicates: readonly string[];
  selectedCounts: ReadonlyMap<string, number>;
  orderedFiles: readonly File[];
  valid: boolean;
}

interface SelectedPackage {
  packageImportId: string;
  files: readonly File[];
  validation: OfficialExportFileValidation;
}

interface ConfirmedPackage {
  selected: SelectedPackage;
  result: OfficialExportImportResult;
}

const NORMALIZED_COUNT_LABELS: Record<string, string> = {
  suppliers: "仕入先",
  supplier_variant_costs: "商品と仕入先の仕入条件",
  product_variants: "商品・バリエーション",
  purchase_orders: "発注書",
  purchase_order_items: "発注明細",
  purchase_order_money_fences: "金額不確定POの安全フェンス",
  stock_transfers: "在庫移動",
  stock_transfer_items: "在庫移動明細",
  stocktakes: "棚卸し",
  stock_take_items: "棚卸し明細",
  stock_adjustments: "在庫調整",
  stock_adjustment_items: "在庫調整明細",
  reorder_policies: "発注点設定",
  variant_suppliers: "商品と仕入先の仕入条件",
  min_max_locations: "拠点別Min/Max履歴",
};

function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

export function validateOfficialExportFiles(
  files: readonly File[],
): OfficialExportFileValidation {
  const selectedCounts = new Map<string, number>();
  for (const file of files) {
    selectedCounts.set(file.name, (selectedCounts.get(file.name) ?? 0) + 1);
  }
  const required = new Set<string>(OFFICIAL_EXPORT_FILE_NAMES);
  const missing = OFFICIAL_EXPORT_FILE_NAMES.filter(
    (name) => !selectedCounts.has(name),
  );
  const unknown = uniqueSorted(
    files.map((file) => file.name).filter((name) => !required.has(name)),
  );
  const duplicates = uniqueSorted(
    [...selectedCounts.entries()]
      .filter(([, count]) => count > 1)
      .map(([name]) => name),
  );
  const orderedFiles = OFFICIAL_EXPORT_FILE_NAMES.flatMap((name) => {
    const matches = files.filter((file) => file.name === name);
    return matches.length === 1 ? matches : [];
  });
  return {
    missing,
    unknown,
    duplicates,
    selectedCounts,
    orderedFiles,
    valid:
      missing.length === 0 &&
      unknown.length === 0 &&
      duplicates.length === 0 &&
      orderedFiles.length === OFFICIAL_EXPORT_FILE_NAMES.length,
  };
}

function requireNonNegativeInteger(value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(t("サーバーの確認結果を検証できませんでした。もう一度確認してください。"));
  }
}

function assertResult(
  result: OfficialExportImportResult,
  packageImportId: string,
  phase: "dry-run" | "apply",
): void {
  const isFreshDryRun =
    phase === "dry-run" &&
    result.dry_run === true &&
    result.already_applied === false &&
    typeof result.plan_token === "string" &&
    result.plan_token.length > 0 &&
    result.archived_files === 0;
  const isCommittedDryRunReplay =
    phase === "dry-run" &&
    result.dry_run === true &&
    result.already_applied === true &&
    result.plan_token === null &&
    result.archived_files === OFFICIAL_EXPORT_FILE_NAMES.length;
  const isApplyResult =
    phase === "apply" &&
    result.dry_run === false &&
    result.plan_token === null &&
    result.archived_files === OFFICIAL_EXPORT_FILE_NAMES.length;
  if (
    result.package_import_id !== packageImportId ||
    result.file_count !== OFFICIAL_EXPORT_FILE_NAMES.length ||
    result.files.length !== OFFICIAL_EXPORT_FILE_NAMES.length ||
    (!isFreshDryRun && !isCommittedDryRunReplay && !isApplyResult) ||
    !/^[a-f0-9]{64}$/.test(result.package_sha256) ||
    !/^\d+$/.test(result.stocky_account_id)
  ) {
    throw new Error(t("サーバーの確認結果を検証できませんでした。もう一度確認してください。"));
  }

  const returnedNames = result.files.map((file) => file.file_name);
  const handlingModes = new Set([
    "normalized_and_retained",
    "partially_normalized_and_retained",
    "retained",
  ]);
  if (
    new Set(returnedNames).size !== OFFICIAL_EXPORT_FILE_NAMES.length ||
    OFFICIAL_EXPORT_FILE_NAMES.some((name) => !returnedNames.includes(name))
  ) {
    throw new Error(t("サーバーの確認結果を検証できませんでした。もう一度確認してください。"));
  }
  requireNonNegativeInteger(result.total_rows);
  requireNonNegativeInteger(result.archived_files);
  for (const file of result.files) {
    if (file.file_name === "manifest.json") {
      if (file.row_count !== null) {
        throw new Error(t("サーバーの確認結果を検証できませんでした。もう一度確認してください。"));
      }
    } else if (file.row_count === null) {
      throw new Error(t("サーバーの確認結果を検証できませんでした。もう一度確認してください。"));
    } else {
      requireNonNegativeInteger(file.row_count);
    }
    if (
      !handlingModes.has(file.handling) ||
      !/^[a-f0-9]{64}$/.test(file.sha256)
    ) {
      throw new Error(t("サーバーの確認結果を検証できませんでした。もう一度確認してください。"));
    }
  }
  const fileRowTotal = result.files.reduce(
    (total, file) => total + (file.row_count ?? 0),
    0,
  );
  if (fileRowTotal !== result.total_rows) {
    throw new Error(t("サーバーの確認結果を検証できませんでした。もう一度確認してください。"));
  }
  for (const count of Object.values(result.normalized_counts)) {
    requireNonNegativeInteger(count);
  }
  for (const count of Object.values(result.action_counts)) {
    requireNonNegativeInteger(count);
  }
  requireNonNegativeInteger(result.unmatched_variant_count);
  requireNonNegativeInteger(result.unmatched_supplier_count);
}

function modeLabel(mode: string): string {
  if (mode === "normalized_and_retained") return t("業務データと原本");
  if (mode === "partially_normalized_and_retained") {
    return t("一部の業務データと原本");
  }
  if (mode === "retained") return t("原本のみ");
  return mode;
}

const LOCALIZABLE_WARNINGS = new Set([
  "product_variants.csvの商品・価格・在庫数は履歴として保存し、Shopify商品は変更しません。",
  "min_max_available.csvは拠点別履歴として保存し、現在の在庫数や発注点は変更しません。",
  "stock_take_items.csvは棚卸履歴として保存し、現在の在庫数へ反映しません。",
  "stock_transfer_items.csvは移動履歴として保存し、現在の在庫移動を作成しません。",
]);

function localizedWarning(warning: string): string {
  const variant = /^Shopify variant (\d+): 発注点がないためMOQ\/リードタイムを運用設定へ反映しません。$/.exec(
    warning,
  );
  if (variant) {
    return t(
      "Shopify variant {id}: 発注点がないためMOQ/リードタイムを運用設定へ反映しません。",
      { id: variant[1] },
    );
  }
  return LOCALIZABLE_WARNINGS.has(warning) ? t(warning) : warning;
}

function ResultDetails({ result }: { result: OfficialExportImportResult }) {
  const fileByName = new Map(
    result.files.map((file) => [file.file_name, file] as const),
  );
  const fileRows = OFFICIAL_EXPORT_FILE_NAMES.map((name) => {
    const file = fileByName.get(name);
    return [
      name,
      file?.row_count?.toLocaleString("en-US") ?? "—",
      file ? modeLabel(file.handling) : "—",
    ];
  });
  const normalizedRows = Object.entries(result.normalized_counts).map(
    ([resource, count]) => [
      t(NORMALIZED_COUNT_LABELS[resource] ?? resource),
      count.toLocaleString("en-US"),
    ],
  );

  return (
    <BlockStack gap="300">
      <Text as="p">
        {t("9ファイル・元データ{rows}行を確認しました。", {
          rows: result.total_rows.toLocaleString("en-US"),
        })}
      </Text>
      <Text as="p">
        {t("運用データ: 新規{created}件・更新{updated}件・変更なし{unchanged}件。商品未一致{variants}件・仕入先未一致{suppliers}件。", {
          created: result.action_counts.create ?? 0,
          updated: result.action_counts.update ?? 0,
          unchanged: result.action_counts.unchanged ?? 0,
          variants: result.unmatched_variant_count,
          suppliers: result.unmatched_supplier_count,
        })}
      </Text>
      <DataTable
        columnContentTypes={["text", "numeric", "text"]}
        headings={[t("ファイル名"), t("元データ行数"), t("保存方法")]}
        rows={fileRows}
      />
      {normalizedRows.length > 0 && (
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {t("UreyukiBoxで保存する件数")}
          </Text>
          <DataTable
            columnContentTypes={["text", "numeric"]}
            headings={[t("データ"), t("件数")]}
            rows={normalizedRows}
          />
        </BlockStack>
      )}
      {result.warnings.length > 0 && (
        <BlockStack gap="100">
          <Text as="h3" variant="headingSm">
            {t("確認が必要な項目があります")}
          </Text>
          <List type="bullet">
            {result.warnings.map((warning, index) => (
              <List.Item key={`${index}-${warning}`}>
                {localizedWarning(warning)}
              </List.Item>
            ))}
          </List>
        </BlockStack>
      )}
    </BlockStack>
  );
}

export function OfficialExportImport({ onImported }: Props) {
  const [selected, setSelected] = useState<SelectedPackage | null>(null);
  const [confirmed, setConfirmed] = useState<ConfirmedPackage | null>(null);
  const [applied, setApplied] = useState<OfficialExportImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const validation = selected?.validation ?? validateOfficialExportFiles([]);
  const selectionSummary = useMemo(() => {
    if (selected === null) return null;
    return t("{selected} / 9ファイルを選択", {
      selected: selected.files.length,
    });
  }, [selected]);

  const selectFiles = useCallback((files: FileList | null) => {
    const nextFiles = Array.from(files ?? []);
    if (nextFiles.length === 0) return;
    setSelected({
      packageImportId: globalThis.crypto.randomUUID(),
      files: nextFiles,
      validation: validateOfficialExportFiles(nextFiles),
    });
    setConfirmed(null);
    setApplied(null);
    setError(null);
  }, []);

  const dryRun = useCallback(async () => {
    if (!selected?.validation.valid || busyRef.current) return;
    const snapshot = selected;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setApplied(null);
    try {
      const result = await dryRunOfficialExport({
        packageImportId: snapshot.packageImportId,
        files: snapshot.validation.orderedFiles,
      });
      assertResult(result, snapshot.packageImportId, "dry-run");
      if (result.already_applied) {
        setApplied(result);
        setConfirmed(null);
        onImported();
      } else {
        setConfirmed({ selected: snapshot, result });
      }
    } catch (cause) {
      setError(csvErrorMessage(cause));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [onImported, selected]);

  const apply = useCallback(async () => {
    if (confirmed === null || busyRef.current) return;
    const planToken = confirmed.result.plan_token;
    if (!planToken) return;
    const snapshot = confirmed;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await applyOfficialExport({
        packageImportId: snapshot.selected.packageImportId,
        files: snapshot.selected.validation.orderedFiles,
        planToken,
      });
      assertResult(result, snapshot.selected.packageImportId, "apply");
      setApplied(result);
      setConfirmed(null);
      onImported();
    } catch (cause) {
      // 通信再試行では同じpackage ID・Fileオブジェクト・plan tokenを再利用する。
      // 409 は確認後に保存先の状態が変わった合図。選択した9ファイルは保持しつつ
      // 古い確認tokenだけを破棄し、次の操作を必ず再dry-runに戻す。
      if (cause instanceof ApiError && cause.status === 409) {
        setConfirmed(null);
      }
      setError(csvErrorMessage(cause));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [confirmed, onImported]);

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="h2" variant="headingLg">
            {t("9/1以降のStocky公式エクスポート")}
          </Text>
          <Text as="p">
            {t("Stockyから届いたフォルダ内のmanifest.jsonと8つのCSVを、名前を変えずにまとめて選択してください。ZIPのままでは取り込めません。")}
          </Text>
          <Text as="p" tone="subdued">
            {t("最初に内容と件数だけを確認します。確認画面の次の操作を行うまで保存しません。現在のShopify在庫数は変更しません。")}
          </Text>
        </BlockStack>

        <BlockStack gap="100">
          <Text as="p" fontWeight="semibold">
            {t("公式エクスポートの9ファイル")}
          </Text>
          <input
            type="file"
            multiple
            accept=".json,.csv,application/json,text/csv"
            aria-label={t("Stocky公式エクスポートの9ファイル")}
            disabled={busy}
            onChange={(event) => {
              selectFiles(event.currentTarget.files);
              // 同じパッケージを明示的に選び直す場合にもchangeを発火させる。
              event.currentTarget.value = "";
            }}
          />
          {selectionSummary && (
            <Text as="p" tone="subdued">
              {selectionSummary}
            </Text>
          )}
        </BlockStack>

        <section aria-label={t("必要なファイルの選択状況")}>
          <BlockStack gap="150">
            {OFFICIAL_EXPORT_FILE_NAMES.map((name) => {
              const count = validation.selectedCounts.get(name) ?? 0;
              const selectedOnce = count === 1;
              return (
                <InlineStack key={name} gap="200" blockAlign="center">
                  <Badge tone={selectedOnce ? "success" : "attention"}>
                    {selectedOnce
                      ? t("選択済み")
                      : count > 1
                        ? t("重複")
                        : t("未選択")}
                  </Badge>
                  <Text as="span">{name}</Text>
                </InlineStack>
              );
            })}
          </BlockStack>
        </section>

        {selected && !validation.valid && (
          <Banner tone="critical" title={t("9ファイルを確認してください")}>
            <List type="bullet">
              {validation.missing.length > 0 && (
                <List.Item>
                  {t("不足: {files}", {
                    files: validation.missing.join(", "),
                  })}
                </List.Item>
              )}
              {validation.duplicates.length > 0 && (
                <List.Item>
                  {t("重複: {files}", {
                    files: validation.duplicates.join(", "),
                  })}
                </List.Item>
              )}
              {validation.unknown.length > 0 && (
                <List.Item>
                  {t("対象外: {files}", {
                    files: validation.unknown.join(", "),
                  })}
                </List.Item>
              )}
            </List>
          </Banner>
        )}

        {confirmed && (
          <Banner
            tone="warning"
            title={t("まだ保存していません。内容と件数を確認してください")}
          >
            <ResultDetails result={confirmed.result} />
          </Banner>
        )}

        {error && (
          <Banner tone="critical" title={t("取り込みに失敗しました")}>
            <Text as="p">{error}</Text>
          </Banner>
        )}

        {applied && (
          <Banner tone="success" title={t("公式エクスポートを取り込みました")}>
            <BlockStack gap="200">
              <Text as="p">
                {t("9ファイルの原本を保存しました。現在のShopify在庫数は変更していません。")}
              </Text>
              {applied.already_applied && (
                <Text as="p">
                  {t("同じ取込の保存済み結果を再表示しました。重複して追加していません。")}
                </Text>
              )}
              <ResultDetails result={applied} />
            </BlockStack>
          </Banner>
        )}

        {!applied && (
          <InlineStack align="end">
            <Button
              variant="primary"
              disabled={!validation.valid || busy}
              loading={busy}
              onClick={confirmed ? apply : dryRun}
            >
              {confirmed ? t("確認して取り込む") : t("ファイル内容を確認する")}
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    </Card>
  );
}
